import {Http, HttpModule} from "@angular/http";
import {
    Compiler,
    ComponentFactoryResolver,
    Injector,
    ModuleWithProviders,
    NgModule,
    SystemJsNgModuleLoader
} from "@angular/core";
import {CommonModule, DatePipe} from "@angular/common";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {RouterModule} from "@angular/router";
import {TranslateModule} from "@ngx-translate/core";
import {CookieService} from "angular2-cookie/core";

import {ClipboardModule} from "ngx-clipboard";
import {FileDropModule} from "ngx-file-drop";
import {PerfectScrollbarModule} from "ngx-perfect-scrollbar";
import {
    BlockUIModule,
    CalendarModule,
    CheckboxModule,
    DataGridModule,
    DataListModule,
    DataScrollerModule,
    DataTableModule,
    DialogModule,
    DropdownModule,
    InputSwitchModule,
    LightboxModule,
    ListboxModule,
    PanelModule,
    ProgressBarModule,
    RadioButtonModule,
    SelectButtonModule,
    SliderModule,
    TooltipModule
} from "primeng/primeng";
import {Subject} from "rxjs";

import {NgSharedModule} from "../ng-shared/ng-shared.module";
import {NgAppDetailsModule} from "../ng-app-details/ng-app-details.module";
import {NgClickOutsideModule} from "../ng-shared/ng-directives/ng-click-outside/ng-click-outside.module";
import {NgOnlyNumberModule} from "../ng-shared/ng-directives/ng-only-number/ng-only-number.module";
import {NgHamburgerMenuModule} from "../ng-app-hamburger-menu/ng-app-hamburger-menu.module";
import {NgWaitModule} from "../ng-shared/ng-components/ng-wait/ng-wait.module";
import {NgAppMonitoringModule} from "../ng-app-monitoring/ng-app-monitoring.module";

import {NgAuthGuardService} from "../ng-shared/ng-services/ng-auth-guard.service";
import {NgTenantsGuardService} from "../ng-shared/ng-services/ng-tenants-guard.service";
import {NgSecurityService} from "../ng-shared/ng-services/ng-security.service";
import {NgWindowRefService} from "../ng-shared/ng-services/ng-window-ref.service";
import {TciContribRequestService} from "./tci-contrib-request.service";
import {TCITenantServiceLoader} from "./tci-tenant-service-loader";

import {TCITenantBaseVPNServiceProvider} from "./ng-shared-services/tci-vpn-provider.service";
import {TciContribService} from "./tci-contrib.service";
import {TciContribModelService} from "./tci-contrib.model";
import {TciTenantService} from "./tci-tenant.service";
import {TciTenantModelService} from "./tci-tenant.model";
import {TCITenantRemoteServices} from "./tci-tenant-remote-services";
import {TCITenantMenuServices} from "./tci-tenant.menu";
import {TciTenantComponentCacheService} from "./tci-tenant-component-cache.service";
import {IMessaging, TCIAppMessaging} from "@tci/common";
import {NgEnvVarsService} from "../ng-shared/ng-services/ng-env-vars.service";
import {NgToastrService} from "../ng-shared/ng-components/ng-toastr/ng-toastr.service";
import {NgOrgChangeNotificationService} from "../ng-shared/ng-services/ng-org-change-notification.service";
import {NgUiDiscovery} from "../ng-shared/ng-services/ng-ui-discovery";
import {NgUserService} from "../ng-shared/ng-services/ng-user.service";
import {NgPaginatorModule} from "../ng-shared/ng-components/ng-paginator/ng-paginator.module";
import {NgBaseAgentMenuModule} from "../ng-env-tools/base-agent/base-agent-menu.module";
import {NgCategoryIconModule} from "../ng-shared/ng-directives/ng-category-icon/ng-category-icon.module";
import {NgVisibilityIconModule} from "../ng-shared/ng-directives/ng-visibility-icon/ng-visibility-icon.module";
import {NgBaseSupplementMenuModule} from "../ng-env-tools/base-supplement/base-supplement-menu/base-supplement-menu.module";
import {NgMarketplaceV2Module} from "../ng-marketplace-v2/ng-marketplace-v2.module";


export function ComponentCacheServiceFactory(
    loader: SystemJsNgModuleLoader,
    compiler: Compiler,
    tenantRemoteServices: TCITenantRemoteServices,
    tenantSvc: TciTenantService,
    tenantModelSvc: TciTenantModelService
) {
    return TciTenantComponentCacheService.getInstance(loader, compiler, tenantRemoteServices, tenantSvc, tenantModelSvc);
}

export function NgContribServiceFactory(
    _injector: Injector,
    http: Http,
    resolver: ComponentFactoryResolver,
    loader: SystemJsNgModuleLoader,
    compiler: Compiler,
    modelService: TciContribModelService,
    cookieService: CookieService,
    contribReqService: TciContribRequestService,
    _window: NgWindowRefService
) {
    TciContribService.getInstance(
        _injector,
        http,
        resolver,
        loader,
        compiler,
        modelService,
        cookieService,
        contribReqService,
        _window
    );
}

export function NgContributionsRequestServiceFactory(http: Http, injector: Injector) {
    return TciContribRequestService.getInstance(http, injector);
}

export function tciTenantServiceLoaderFactory(
    _injector: Injector,
    http: Http,
    resolver: ComponentFactoryResolver,
    loader: SystemJsNgModuleLoader,
    compiler: Compiler,
    _window: NgWindowRefService,
    remoteSvcs: TCITenantRemoteServices,
    modelSrc: TciTenantModelService
) {
    return TCITenantServiceLoader.getInstance(
        _injector,
        http,
        resolver,
        loader,
        compiler,
        _window,
        remoteSvcs,
        modelSrc
    );
}

export function tenantServiceFactory(
    http: Http,
    resolver: ComponentFactoryResolver,
    loader: SystemJsNgModuleLoader,
    compiler: Compiler,
    windowRef: NgWindowRefService,
    modelService: TciTenantModelService,
    remoteServiceLoader: TCITenantServiceLoader,
    userService: NgUserService
) {
    return TciTenantService.getInstance(http, resolver, loader, compiler, windowRef, modelService, remoteServiceLoader, userService);
}

export function tenanModelServiceFactory(messagingService: IMessaging, envVars: NgEnvVarsService) {
    return TciTenantModelService.getInstance(messagingService, envVars);
}

export function ngAppMessagingFactory() {
    return TCIAppMessaging.getInstance(new Subject());
}

function ngOrgChangeNotificationServiceFactory(ngUserService: NgUserService): NgOrgChangeNotificationService {
    return NgOrgChangeNotificationService.getInstance(ngUserService);
}

function ngUiDiscoveryFactory(http: Http, envVarService: NgEnvVarsService, toastr: NgToastrService, userService: NgUserService) {
    return NgUiDiscovery.getInstance(http, envVarService, toastr, userService);
}

const modules = [
    // Angular Modules
    HttpModule,
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,

    // Third party modules
    TranslateModule,
    ClipboardModule,
    FileDropModule,
    BlockUIModule,
    CalendarModule,
    CheckboxModule,
    DataListModule,
    DataScrollerModule,
    DataTableModule,
    DialogModule,
    DropdownModule,
    InputSwitchModule,
    LightboxModule,
    ListboxModule,
    PanelModule,
    RadioButtonModule,
    SelectButtonModule,
    PerfectScrollbarModule,
    SliderModule,
    TooltipModule,
    ProgressBarModule,
    DataGridModule,

    // WS modules
    NgSharedModule,
    NgAppDetailsModule,
    NgHamburgerMenuModule,
    NgBaseAgentMenuModule,
    NgBaseSupplementMenuModule,
    NgWaitModule,
    NgClickOutsideModule,
    NgOnlyNumberModule,
    NgAppMonitoringModule,
    NgPaginatorModule,
    NgCategoryIconModule,
    NgVisibilityIconModule,
    NgMarketplaceV2Module
];

/**
 * TCI UI Contribution Module
 * @private
 */
@NgModule({
    imports: [...modules],
    exports: [...modules],
    providers: [
        DatePipe,
        NgAuthGuardService,
        NgTenantsGuardService,
        TciContribModelService,
        TciContribRequestService,
        TCITenantRemoteServices,
        TCITenantMenuServices,
        CookieService,
        {
            provide: "ngAppMessaging",
            useFactory: ngAppMessagingFactory
        },
        {
            provide: TciTenantModelService,
            useFactory: tenanModelServiceFactory,
            deps: [
                "ngAppMessaging",
                NgEnvVarsService
            ]
        },
        {
            provide: TciContribRequestService,
            useFactory: NgContributionsRequestServiceFactory,
            deps: [
                Http,
                Injector
            ]
        },
        {
            provide: NgOrgChangeNotificationService,
            useFactory: ngOrgChangeNotificationServiceFactory,
            deps: []
        },
        {
            provide: TciContribService,
            useFactory: NgContribServiceFactory,
            deps: [
                Injector,
                Http,
                ComponentFactoryResolver,
                SystemJsNgModuleLoader,
                Compiler,
                TciContribModelService,
                CookieService,
                TciContribRequestService,
                NgWindowRefService
            ]
        },
        {
            provide: TCITenantRemoteServices,
            useFactory: ngUiDiscoveryFactory,
            deps: [
                Http,
                NgEnvVarsService,
                NgToastrService,
                NgUserService
            ]
        },
        {
            provide: TciTenantComponentCacheService,
            useFactory: ComponentCacheServiceFactory,
            deps: [
                SystemJsNgModuleLoader,
                Compiler,
                TCITenantRemoteServices,
                TciTenantService,
                TciTenantModelService
            ]
        },
        {
            provide: TCITenantServiceLoader,
            useFactory: tciTenantServiceLoaderFactory,
            deps: [
                Injector,
                Http,
                ComponentFactoryResolver,
                SystemJsNgModuleLoader,
                Compiler,
                NgWindowRefService,
                TCITenantRemoteServices,
                TciTenantModelService
            ]
        },
        {
            provide: TciTenantService,
            useFactory: tenantServiceFactory,
            deps: [
                Http,
                ComponentFactoryResolver,
                SystemJsNgModuleLoader,
                Compiler,
                NgWindowRefService,
                TciTenantModelService,
                TCITenantServiceLoader,
                NgUserService
            ]
        },
        {
            provide: "$scope",
            useExisting: "$rootScope"
        },
        {
            provide: TCITenantBaseVPNServiceProvider,
            useClass: NgSecurityService
        }
    ]
})
export class TciTenantModule {
    /**
     * Contrib Module constructor
     * @param {Http} http
     */
    public constructor(http: Http) {
    }

    public static forRoot(): ModuleWithProviders {
        return {
            ngModule: TciTenantModule,
            providers: [
                NgTenantsGuardService,
                TciContribModelService,
                CookieService,
                {
                    provide: "ngAppMessaging",
                    useFactory: ngAppMessagingFactory
                },
                {
                    provide: TciTenantModelService,
                    useFactory: tenanModelServiceFactory,
                    deps: [
                        "ngAppMessaging",
                        NgEnvVarsService
                    ]
                },
                {
                    provide: TciContribRequestService,
                    useFactory: NgContributionsRequestServiceFactory,
                    deps: [
                        Http,
                        Injector
                    ]
                },
                {
                    provide: TciContribService,
                    useFactory: NgContribServiceFactory,
                    deps: [
                        Injector,
                        Http,
                        ComponentFactoryResolver,
                        SystemJsNgModuleLoader,
                        Compiler,
                        TciContribModelService,
                        CookieService,
                        TciContribRequestService,
                        NgWindowRefService
                    ]
                },
                {
                    provide: TCITenantServiceLoader,
                    useFactory: tciTenantServiceLoaderFactory,
                    deps: [
                        Injector,
                        Http,
                        ComponentFactoryResolver,
                        SystemJsNgModuleLoader,
                        Compiler,
                        NgWindowRefService,
                        TCITenantRemoteServices,
                        TciTenantModelService
                    ]
                },
                {
                    provide: TciTenantService,
                    useFactory: tenantServiceFactory,
                    deps: [
                        Http,
                        ComponentFactoryResolver,
                        SystemJsNgModuleLoader,
                        Compiler,
                        NgWindowRefService,
                        TciTenantModelService,
                        TCITenantServiceLoader,
                        NgUserService
                    ]
                },
                {
                    provide: TCITenantRemoteServices,
                    useFactory: ngUiDiscoveryFactory,
                    deps: [
                        Http,
                        NgEnvVarsService,
                        NgToastrService,
                        NgUserService
                    ]
                },
                {
                    provide: TciTenantComponentCacheService,
                    useFactory: ComponentCacheServiceFactory,
                    deps: [
                        SystemJsNgModuleLoader,
                        Compiler,
                        TCITenantRemoteServices,
                        TciTenantService,
                        TciTenantModelService
                    ]
                },
                {
                    provide: "$scope",
                    useExisting: "$rootScope"
                },
                {
                    provide: TCITenantBaseVPNServiceProvider,
                    useClass: NgSecurityService
                }
            ]
        };
    }

}
